import {
  ArpMediaSubType,
  InternalArpMedia,
  InternalArpMetadata,
  RegisteredArpMetadata,
  StemType
} from '../types'
import { deserializeStemSnapshot } from './serialization'

/////////////////////////////
//    Helper functions     //
/////////////////////////////

// Prefix for Arweave data, followed by Arweave hash
const ARWEAVE_PREFIX = 'ar://'
const ARWEAVE_URL_PREFIX = 'https://arweave.net/'

// Prefix for IPFS data, followed by IPFS hash
// const IPFS_PREFIX = 'ipfs://'
// const IPFS_URL_PREFIX = 'https://ipfs.io/ipfs/'

/**
 * Gets a valid URL from the given ArpMedia that can
 * be fetched from the web. Prioritizes a cached URL (e.g. S3) if available,
 * to improve performance.
 */
export const getUrlForMedia = (
  arpMedia: Pick<
    InternalArpMedia,
    'index' | 'metadata' | 'cachedMp3DataUri' | 'cachedWavDataUri' | 'dataUri'
  >
): string => {
  // Temporary workaround for new samples, which will have bad Mp3s temporarily.
  const forceUseWav = arpMedia.metadata.mediaType === 'one-shot' && arpMedia.index > 410

  // Prioritize Cached Mp3 -> Cached Wav -> arweave uri
  if (arpMedia.cachedMp3DataUri && !forceUseWav) {
    console.log('using cached mp3', arpMedia.cachedMp3DataUri)
    return arpMedia.cachedMp3DataUri
  } else if (arpMedia.cachedWavDataUri) {
    console.log('using cached wav', arpMedia.cachedWavDataUri)
    return arpMedia.cachedWavDataUri
  } else {
    return dataUriToUrl(arpMedia.dataUri)
  }
}

/**
 * Gets a valid URL from the given dataUri string. The dataUri can
 * be an Arweave address of the form "ar://1234", matching the opensea metadata spec
 * here:  https://docs.opensea.io/docs/metadata-standards#ipfs-and-arweave-uris
 */
export const dataUriToUrl = (dataUri: string): string => {
  if (dataUri.substring(0, ARWEAVE_PREFIX.length) === ARWEAVE_PREFIX) {
    return ARWEAVE_URL_PREFIX + dataUri.substring(ARWEAVE_PREFIX.length)
    // } else if (dataUri.substring(0, IPFS_PREFIX.length) === IPFS_PREFIX) {
    //   return IPFS_URL_PREFIX + dataUri.substring(IPFS_PREFIX.length)
  } else {
    return dataUri
  }
}

/**
 * Converts from the given URL to a data URI.
 * Inverse of dataUriToUrl().
 *
 * See opensea metadata spec:
 * https://docs.opensea.io/docs/metadata-standards#ipfs-and-arweave-uris
 */
export const urlToDataUri = (url: string): string => {
  if (url.substring(0, ARWEAVE_URL_PREFIX.length) === ARWEAVE_URL_PREFIX) {
    return ARWEAVE_PREFIX + url.substring(ARWEAVE_URL_PREFIX.length)
    // } else if (url.substring(0, IPFS_URL_PREFIX.length) === IPFS_URL_PREFIX) {
    //   return IPFS_PREFIX + url.substring(IPFS_URL_PREFIX.length)
  } else {
    return url
  }
}

/**
 * Gets only the properties of the metadata format that we'd like to mint to the chain.
 * Removes ArpeggiComposition.
 */
export const convertInternalMetadataToArpMetadata = (
  metadata: InternalArpMetadata
): RegisteredArpMetadata => {
  return {
    title: metadata.title,
    bpm: metadata.bpm,
    key: metadata.key,
    genres: metadata.genres,
    tags: metadata.tags,
    mediaType: metadata.mediaType || ''
  }
}

/**
 * Currently, we store a bunch of different type of information in the `metadata.tags` property
 * of metadata, including instruments, project name and type, etc. This gets just the instrument list from it.
 
 * TODO: Do this parsing immediately when we read from the blockchain in OCEL and write this to our database as
 * explicit fields.
 */
/**
 * Gets the list of instruments from the metadata.
 */
export const getInstrumentsFromMedia = (media: { metadata: { tags: string[] } }): string[] => {
  // If a tag doesn't contain ":" (not a key:value store) then assume it's an instrument.
  return media.metadata.tags.filter((tag) => !tag.includes(':'))
}
/**
 * Gets the primary instrument from the metadata, or undefined if it doesn't exist.
 */
export const getPrimaryInstrumentFromMedia = (media: {
  metadata: { tags: string[] }
}): string | undefined => {
  const instruments = getInstrumentsFromMedia(media)
  if (instruments.length > 0) {
    return instruments[0]
  }
  return undefined
}

/** Reads the arpeggi composition to determine which subtype to set for this stem, or undefined if no subtype should be added. */
export function getArpMediaSubType(arpMedia: InternalArpMedia): ArpMediaSubType | undefined {
  // Songs and one-shots don't need a subtype field (for now)
  if (arpMedia.metadata.subType !== 'stem' && arpMedia.metadata.mediaType !== 'stem') {
    return undefined
  }

  // Clip: has no arpeggi composition
  if (!isValidComposition(arpMedia?.metadata?.arpeggiComposition)) {
    return ArpMediaSubType.CLIP
  }

  // Determine the stem type based on the actual contents of the composition.
  const stem = deserializeStemSnapshot(arpMedia.metadata.arpeggiComposition)

  if (!stem) {
    return undefined
  }

  if (stem.stemType === StemType.AUDIO) {
    return ArpMediaSubType.AUDIO
  } else if (stem.stemType === StemType.DRUMS) {
    return ArpMediaSubType.MIDI_DRUM
  } else if (stem.stemType === StemType.INSTRUMENT) {
    return ArpMediaSubType.MIDI_INSTRUMENT
  } else {
    return undefined
  }
}

/** Returns true if the given media has an arpeggi composition. */
export const hasComposition = (media: InternalArpMedia): boolean => {
  return isValidComposition(media?.metadata?.arpeggiComposition)
}

/** Returns true if the given serialized composition string is a valid arpeggi composition. */
export const isValidComposition = (arpeggiComposition?: string | Object): boolean => {
  return arpeggiComposition ? arpeggiComposition !== '"0x"' : false
}
